import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Chip, Divider, TextField } from '@mui/material';
import { Block, Close, Search } from '@mui/icons-material';
import { DEFAULT_ASSIGNED_TO, EVERYONE_OPTION } from '../../utils/constants';
import { normalizeString } from '../../utils/InstructorGradebookHelpers';
import {
  StyledAutocomplete,
  OptionContainer,
  OptionName,
  OptionGroup,
  CloseIconRoot,
  OptionTextContainer,
  DisabledIcon,
  DisabledIconContainer,
  SearchInputLabelContainer,
  SearchInputLabel,
  autocompleteSlotProps,
} from './styles';
import MLTooltip from '../MLTooltip';

export default function RosterSearchAutocomplete({
  groups,
  students,
  onSelect,
  isAssign = false,
  preloadedItems = [],
  disabled = false,
  searchInputLabel = '',
}) {
  const theme = useTheme();
  const [selectedItems, setSelectedItems] = useState([EVERYONE_OPTION]);
  const slotProps = useMemo(() => autocompleteSlotProps(theme), [theme]);

  useEffect(() => {
    if (preloadedItems.length > 0 && isAssign) {
      handleSelect(null, preloadedItems);
    }
  }, [preloadedItems]);

  const options = useMemo(
    () => [
      EVERYONE_OPTION,
      ...groups.map((group) => ({
        ...group,
        type: 'group',
      })),
      ...students.map((student) => ({ ...student, type: 'student' })),
    ],
    [students, groups],
  );

  const handleSelect = (_event, newValue) => {
    const lastItem = newValue[newValue.length - 1];
    if (lastItem?.disabledBy) {
      return;
    }
    const isEveryoneSelected = newValue.some(
      (item) => item.studentId === EVERYONE_OPTION.studentId,
    );

    if (
      isEveryoneSelected &&
      (newValue[0].studentId !== DEFAULT_ASSIGNED_TO || newValue.length === 1)
    ) {
      setSelectedItems([EVERYONE_OPTION]);
      onSelect([EVERYONE_OPTION]);
      return;
    }

    const updatedSelectedItems = newValue.filter(
      (item) => item.studentId !== EVERYONE_OPTION.studentId,
    );

    const newlySelectedGroups = updatedSelectedItems.filter(
      (item) => item.type === 'group',
    );

    if (newlySelectedGroups.length > 0) {
      const studentsToRemove = newlySelectedGroups.flatMap(
        (group) => group.studentIds,
      );
      const updatedSelectedItemsFiltered = updatedSelectedItems.filter(
        (item) => !studentsToRemove.includes(item.studentId),
      );

      setSelectedItems(updatedSelectedItemsFiltered);
      onSelect(updatedSelectedItemsFiltered);
    } else {
      setSelectedItems(updatedSelectedItems);
      onSelect(updatedSelectedItems);
    }
  };

  const checkIfGroupDisabled = (option) => {
    return selectedItems.find((selectedItem) => {
      return option.studentIds?.some((studentId) =>
        selectedItem.studentIds?.includes(studentId),
      );
    });
  };

  const filterOptions = (options, { inputValue }) => {
    const filtered = options.filter((option) => {
      const isStudent = option.type === 'student';
      const isInSelectedGroup = selectedItems.some(
        (item) =>
          item.type === 'group' && item.studentIds.includes(option.studentId),
      );
      const isGroup = option.type === 'group';
      const isDisabled = isAssign && isGroup && checkIfGroupDisabled(option);
      option.disabledBy = isDisabled;
      const matchesInput =
        option.name &&
        normalizeString(option.name.toLowerCase()).includes(
          normalizeString(inputValue).toLowerCase(),
        );
      return isStudent ? !isInSelectedGroup && matchesInput : matchesInput;
    });
    return filtered;
  };

  const renderOption = (props, option) => {
    const { key, ...restProps } = props;
    const isGroup = option.type === 'group';
    const groupSize = option.studentIds?.length || 0;
    const groupText = `Group with ${groupSize} ${groupSize > 1 ? 'students' : 'student'}`;
    const isDisabled = !!option.disabledBy;

    const toolTipText =
      isDisabled &&
      `This group cannot be added because it contains students also in ${option?.disabledBy.name}`;

    return (
      <li key={key} {...restProps}>
        <OptionContainer disabled={isDisabled}>
          <OptionTextContainer groupOptionWidth={isGroup && groupSize}>
            <OptionName noWrap variant="body1">
              {option.name}
            </OptionName>
            {isDisabled && (
              <DisabledIconContainer>
                <MLTooltip content={toolTipText} placement="top">
                  <DisabledIcon>
                    <Block data-test-id="roster-search-group-disabled-icon" />
                  </DisabledIcon>
                </MLTooltip>
              </DisabledIconContainer>
            )}
          </OptionTextContainer>
          {isGroup && option.studentId !== DEFAULT_ASSIGNED_TO && (
            <OptionGroup
              noWrap
              variant="body1"
              groupOptionWidth={isGroup && groupSize}
            >
              {groupText}
            </OptionGroup>
          )}
        </OptionContainer>
      </li>
    );
  };

  return (
    <div>
      {searchInputLabel.length > 0 && (
        <SearchInputLabelContainer>
          <SearchInputLabel htmlFor="roster-search-input-autocomplete">
            {searchInputLabel}
          </SearchInputLabel>
        </SearchInputLabelContainer>
      )}
      <StyledAutocomplete
        id="roster-search-input-autocomplete"
        multiple
        disableClearable
        filterSelectedOptions
        disabled={disabled}
        groupBy={(option) => option.type}
        options={options}
        value={selectedItems}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        getOptionLabel={(option) => option.name}
        onChange={handleSelect}
        popupIcon={<Search />}
        slotProps={slotProps}
        renderGroup={({ key, children }) => (
          <li key={key}>
            <ul style={{ padding: 0 }}>{children}</ul>
            {key !== 'student' && <Divider />}
          </li>
        )}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder="" />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.name}
              {...getTagProps({ index })}
              key={option.name}
              deleteIcon={
                <CloseIconRoot>
                  <Close />
                </CloseIconRoot>
              }
            />
          ))
        }
      />
    </div>
  );
}
